import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Ticket, Plus, Copy, X, Users, ToggleLeft, ToggleRight, Trash2, Sparkles } from "lucide-react";

export default function AccessCodesPanel() {
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [redemptions, setRedemptions] = useState({});
  const [form, setForm] = useState({
    code: "",
    scope: "episode",
    episode_number: "",
    max_redemptions: "",
    expires_at: "",
    note: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/access-codes");
      setCodes(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { scope: form.scope };
      if (form.code.trim()) payload.code = form.code.trim().toUpperCase();
      if (form.scope === "episode") {
        if (!form.episode_number) { toast.error("pick an episode number"); setBusy(false); return; }
        payload.episode_number = parseInt(form.episode_number, 10);
      }
      if (form.max_redemptions) payload.max_redemptions = parseInt(form.max_redemptions, 10);
      if (form.expires_at) payload.expires_at = new Date(form.expires_at).toISOString();
      if (form.note.trim()) payload.note = form.note.trim();
      const { data } = await api.post("/admin/access-codes", payload);
      toast.success(`code created: ${data.code}`);
      setForm({ code: "", scope: form.scope, episode_number: "", max_redemptions: "", expires_at: "", note: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "could not create code");
    } finally { setBusy(false); }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/access-codes/${c.id}`, { active: !c.active });
      toast.success(c.active ? "deactivated" : "reactivated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "update failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete code "${c.code}"? This can only be done if it hasn't been redeemed.`)) return;
    try {
      await api.delete(`/admin/access-codes/${c.id}`);
      toast.success("deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "delete failed");
    }
  };

  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success(`copied ${code}`); }
    catch { toast.error("could not copy"); }
  };

  const toggleExpand = async (c) => {
    if (expandedId === c.id) { setExpandedId(null); return; }
    setExpandedId(c.id);
    if (!redemptions[c.id]) {
      try {
        const { data } = await api.get(`/admin/access-codes/${c.id}/redemptions`);
        setRedemptions(prev => ({ ...prev, [c.id]: data.redemptions }));
      } catch { /* ignore */ }
    }
  };

  const scopeLabel = (c) => {
    if (c.scope === "episode") return `Ep ${c.episode_number}`;
    if (c.scope === "any") return "any 1 ep";
    return "full library";
  };

  const active = codes.filter(c => c.active);
  const inactive = codes.filter(c => !c.active);

  return (
    <div className="mb-10" data-testid="access-codes-panel">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Ticket className="text-hotpink" size={22} />
        <h2 className="brush-title text-3xl md:text-4xl text-white">GIFT <span className="text-hotpink">CODES.</span></h2>
        <span className="scribble text-hotpink text-lg ml-auto">{active.length} active</span>
      </div>

      {/* Create form */}
      <div className="bg-coal border border-hotpink/40 p-5 mb-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 font-outfit mb-3">
          <Plus size={14} className="text-hotpink"/> new gift code
        </div>
        <form onSubmit={submit} className="space-y-2" data-testid="access-code-form">
          <div className="grid md:grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/50 font-outfit mb-1">code (blank = auto)</label>
              <input value={form.code} onChange={(e) => setForm(f => ({...f, code: e.target.value.toUpperCase()}))} placeholder="LAUNCH2026" maxLength={32} data-testid="ac-code-input" className="w-full bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none uppercase tracking-widest" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/50 font-outfit mb-1">unlocks</label>
              <select value={form.scope} onChange={(e) => setForm(f => ({...f, scope: e.target.value}))} data-testid="ac-scope" className="w-full bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none">
                <option value="episode">A specific episode</option>
                <option value="any">Any single episode (they pick)</option>
                <option value="all">Full library (all episodes)</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/50 font-outfit mb-1">max uses (blank = unlimited)</label>
              <input value={form.max_redemptions} onChange={(e) => setForm(f => ({...f, max_redemptions: e.target.value}))} type="number" min="1" placeholder="unlimited" data-testid="ac-max" className="w-full bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none" />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-2">
            {form.scope === "episode" && (
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-white/50 font-outfit mb-1">episode #</label>
                <input value={form.episode_number} onChange={(e) => setForm(f => ({...f, episode_number: e.target.value}))} type="number" min="1" placeholder="e.g. 15" required data-testid="ac-episode" className="w-full bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none" />
              </div>
            )}
            <div className={form.scope === "episode" ? "" : "md:col-start-1"}>
              <label className="block text-[10px] uppercase tracking-widest text-white/50 font-outfit mb-1">expires (optional)</label>
              <input value={form.expires_at} onChange={(e) => setForm(f => ({...f, expires_at: e.target.value}))} type="datetime-local" data-testid="ac-expires" className="w-full bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none [color-scheme:dark]" />
            </div>
            <div className={form.scope !== "episode" ? "md:col-span-2" : ""}>
              <label className="block text-[10px] uppercase tracking-widest text-white/50 font-outfit mb-1">label / note (optional)</label>
              <input value={form.note} onChange={(e) => setForm(f => ({...f, note: e.target.value}))} placeholder="Instagram giveaway" maxLength={200} data-testid="ac-note" className="w-full bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none" />
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="ac-submit" className="w-full bg-hotpink hover:bg-hotpink_dark text-white font-outfit font-extrabold uppercase tracking-widest text-xs px-4 py-2 disabled:opacity-60 flex items-center justify-center gap-2">
            <Sparkles size={12} /> {busy ? "..." : "create gift code"}
          </button>
        </form>
      </div>

      {/* Codes list */}
      {codes.length === 0 && (
        <p className="text-white/40 font-outfit text-sm italic">no gift codes yet — create your first above ↑</p>
      )}
      {codes.length > 0 && (
        <div className="bg-coal border border-white/10 p-5 space-y-2">
          {[...active, ...inactive].map(c => {
            const remaining = c.max_redemptions ? Math.max(0, c.max_redemptions - (c.redemption_count || 0)) : null;
            const expired = c.expires_at && new Date(c.expires_at) < new Date();
            return (
              <div key={c.id} className={"border-b border-white/5 pb-3 text-sm font-outfit " + (c.active ? "" : "opacity-50")} data-testid={"ac-row-" + c.id.slice(0, 8)}>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => copyCode(c.code)} data-testid={"ac-copy-" + c.id.slice(0, 8)} className="brush-title text-2xl md:text-3xl text-white hover:text-hotpink flex items-center gap-2 leading-none tracking-widest" title="click to copy">
                    {c.code} <Copy size={14} className="text-white/40"/>
                  </button>
                  <span className="scribble text-hotpink text-base">· {scopeLabel(c)}</span>
                  <span className="ml-auto text-white/40 text-[10px] uppercase tracking-widest">
                    {c.redemption_count || 0} used
                    {c.max_redemptions ? ` / ${c.max_redemptions}` : " · unlimited"}
                    {expired && " · EXPIRED"}
                  </span>
                </div>
                {c.note && <p className="text-white/60 text-xs mt-1">&ldquo;{c.note}&rdquo;</p>}
                <div className="flex items-center gap-3 mt-2 text-[10px] uppercase tracking-widest text-white/50 flex-wrap">
                  {c.expires_at && !expired && <span>expires {new Date(c.expires_at).toLocaleString()}</span>}
                  {remaining !== null && !expired && <span className="text-hotpink">{remaining} left</span>}
                  <button onClick={() => toggleExpand(c)} data-testid={"ac-view-uses-" + c.id.slice(0, 8)} className="ml-auto flex items-center gap-1 text-white/70 hover:text-hotpink">
                    <Users size={11} /> {expandedId === c.id ? "hide uses" : "view uses"}
                  </button>
                  <button onClick={() => toggleActive(c)} data-testid={"ac-toggle-" + c.id.slice(0, 8)} className="flex items-center gap-1 text-white/70 hover:text-hotpink">
                    {c.active ? <ToggleRight size={13} className="text-hotpink"/> : <ToggleLeft size={13}/>}
                    {c.active ? "deactivate" : "reactivate"}
                  </button>
                  {(!c.redemption_count || c.redemption_count === 0) && (
                    <button onClick={() => remove(c)} data-testid={"ac-delete-" + c.id.slice(0, 8)} className="flex items-center gap-1 text-white/70 hover:text-red-500">
                      <Trash2 size={11} /> delete
                    </button>
                  )}
                </div>
                {expandedId === c.id && (
                  <div className="mt-3 bg-ink border border-white/10 p-3">
                    {(redemptions[c.id] || []).length === 0 ? (
                      <p className="text-white/50 text-xs italic">no one has redeemed this yet</p>
                    ) : (
                      <div className="space-y-1">
                        {(redemptions[c.id] || []).map(r => (
                          <div key={r.id} className="text-xs text-white/70 flex items-center gap-2 flex-wrap">
                            <span className="text-hotpink">♡</span>
                            <span className="text-white">{r.user_email || "(anon)"}</span>
                            {r.episode_number && <span className="text-white/50">→ Ep {r.episode_number}</span>}
                            {r.scope === "all" && <span className="text-white/50">→ full library</span>}
                            <span className="text-white/40 ml-auto">{new Date(r.redeemed_at).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
